import React from 'react';
import { 
  Edit, 
  FileText, 
  User, 
  ArrowLeftRight, 
  HelpCircle, 
  Clock,
  ChevronDown,
  AlertCircle,
  ChevronLeft
} from 'lucide-react';

const RequiredFieldMarker = () => (
  <span className="text-red-500 ml-1" aria-label="required field">*</span>
);

function CreateDCQForm({ dcqData, errors, displayParticipantId, handleInputChange, handleSubmit }) {
  // DCQ type options
  const dcqTypes = [
    'Query',
    'Deviation', 
    'Protocol Violation',
    'Data Clarification',
    'Missing Data',
    'Other'
  ];

  const fieldConfigs = [
    { 
      name: 'type', 
      icon: Edit,
      type: 'select',
      options: dcqTypes,
      placeholder: 'Select DCQ type'
    },
    { 
      name: 'action', 
      icon: FileText,
      type: 'text',
      placeholder: 'e.g., Update, Delete, Clarify'
    },
    { 
      name: 'initial_value', 
      icon: Clock,
      type: 'text',
      placeholder: 'Enter the current value'
    },
    { 
      name: 'new_value', 
      icon: ArrowLeftRight,
      type: 'text',
      placeholder: 'Enter the proposed value'
    },
  ];

  const textAreaConfigs = [
    { 
      name: 'requested_change', 
      icon: Edit,
      placeholder: 'Describe the specific change being requested',
      rows: 3
    },
    { 
      name: 'reason_for_change', 
      icon: HelpCircle,
      placeholder: 'Explain why this change is necessary',
      rows: 4
    },
  ];

  const hasErrors = Object.keys(errors).length > 0;

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit(e);
  };

  return (
    <div className="space-y-6">
      {/* Error Summary */}
      {hasErrors && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-red-800">Please correct the following errors:</h3>
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
              {Object.entries(errors).map(([field, error]) => (
                <li key={field}>{error}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Form Fields Grid */}
      <div className="space-y-6">
        {/* Two-column grid for main fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {fieldConfigs.map(({ name, icon: Icon, type, options, placeholder }) => (
            <div key={name} className="relative">
              <label htmlFor={name} className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                {name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                <RequiredFieldMarker />
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
                  <Icon className="text-gray-400 dark:text-gray-500" size={20} />
                </div>
                
                {type === 'select' ? (
                  <>
                    <select
                      id={name}
                      name={name}
                      required
                      className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 appearance-none cursor-pointer text-gray-900 dark:text-gray-100 ${
                        errors[name] 
                          ? 'border-red-500 bg-red-50' 
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                      }`}
                      value={dcqData[name]}
                      onChange={handleInputChange}
                    >
                      <option value="">{placeholder}</option>
                      {options.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <ChevronDown className="text-gray-400 dark:text-gray-500" size={20} />
                    </div>
                  </>
                ) : (
                  <input
                    type={type}
                    id={name}
                    name={name}
                    required
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-gray-900 dark:text-gray-100 ${
                      errors[name] 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                    }`}
                    placeholder={placeholder}
                    value={dcqData[name]}
                    onChange={handleInputChange}
                  />
                )}
              </div>
              {errors[name] && (
                <p className="text-sm text-red-600 mt-1 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors[name]}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Full-width text areas */}
        <div className="space-y-6">
          {textAreaConfigs.map(({ name, icon: Icon, placeholder, rows }) => (
            <div key={name} className="relative">
              <label htmlFor={name} className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                {name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                <RequiredFieldMarker />
              </label>
              <div className="relative">
                <div className="absolute left-3 top-3 pointer-events-none z-10">
                  <Icon className="text-gray-400 dark:text-gray-500" size={20} />
                </div>
                <textarea
                  id={name}
                  name={name}
                  rows={rows}
                  required
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-none text-gray-900 dark:text-gray-100 ${
                    errors[name] 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                  }`}
                  placeholder={placeholder}
                  value={dcqData[name]}
                  onChange={handleInputChange}
                />
              </div>
              {errors[name] && (
                <p className="text-sm text-red-600 mt-1 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors[name]}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Participant ID Field - Read Only */}
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <label htmlFor="participant" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            Participant ID
          </label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
              <User className="text-gray-500 dark:text-gray-400" size={20} />
            </div>
            <input
              type="text"
              name="participant"
              id="participant"
              value={displayParticipantId}
              readOnly
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 cursor-not-allowed"
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">This field is automatically populated and cannot be edited</p>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-all duration-200"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleFormSubmit}
          className="px-6 py-3 border border-transparent rounded-lg shadow-md dark:shadow-gray-900/20 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200"
        >
          Create DCQ
        </button>
      </div>
    </div>
  );
}

// Demo wrapper to show the complete page
export const CreateDCQPage = () => {
  const formProps = {
    dcqData: {
      type: '',
      action: '',
      participant: '123',
      initial_value: '',
      new_value: '',
      requested_change: '',
      reason_for_change: '',
    },
    errors: {},
    displayParticipantId: 'P-2024-001',
    handleInputChange: (e) => console.log('Input changed:', e.target.name, e.target.value),
    handleSubmit: (e) => {
      console.log('Form submitted');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => window.history.back()}
            className="group flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5 mr-1 group-hover:transform group-hover:-translate-x-1 transition-transform duration-200" />           
            <span className="font-medium">Back</span>
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900/20 overflow-hidden">
          <div className="px-6 py-5 bg-primary-600">
            <h2 className="text-xl font-semibold text-white">Create New DCQ</h2>
            <p className="text-primary-100 text-sm mt-1">Fill out the form below to create a new Data Clarification Query</p>
          </div>
          <div className="px-6 py-8 sm:p-8">
            <CreateDCQForm {...formProps} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateDCQForm;